import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { guidedOrderService, type ListGuidedOrdersFilters } from '@/services/guidedOrderService';
import type { GuidedOrder } from '@/types/guidedOrder';

const QUERY_KEY_ORDER = 'guided-order';
const QUERY_KEY_ORDERS = 'guided-orders';
const QUERY_KEY_BY_PRESCRIPTION = 'guided-order-by-prescription';

export function useGuidedOrder(orderId: string | undefined) {
  return useQuery({
    queryKey: [QUERY_KEY_ORDER, orderId],
    queryFn: () => guidedOrderService.getOrder(orderId!),
    enabled: !!orderId,
  });
}

export function useGuidedOrders(filters: ListGuidedOrdersFilters = {}) {
  return useQuery({
    queryKey: [QUERY_KEY_ORDERS, filters],
    queryFn: () => guidedOrderService.listOrders(filters),
  });
}

export function useGuidedOrderByPrescription(prescriptionId: string | undefined) {
  return useQuery({
    queryKey: [QUERY_KEY_BY_PRESCRIPTION, prescriptionId],
    queryFn: () => guidedOrderService.getByPrescription(prescriptionId!),
    enabled: !!prescriptionId,
  });
}

export function useCancelGuidedOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId, reason }: { orderId: string; reason?: string }) =>
      guidedOrderService.cancelOrder(orderId, reason),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY_ORDER, variables.orderId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY_ORDERS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY_BY_PRESCRIPTION] });
    },
  });
}

export function useResendAmendmentConsent() {
  return useMutation({
    mutationFn: (orderId: string) =>
      guidedOrderService.resendAmendmentConsent(orderId),
  });
}

export function useUpdateGuidedOrderReferrer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      orderId,
      referringUserId,
      commissionRateBps,
    }: {
      orderId: string;
      referringUserId: string | null;
      commissionRateBps?: number | null;
    }) =>
      guidedOrderService.updateReferrer(orderId, {
        referring_user_id: referringUserId,
        commission_rate_bps: commissionRateBps,
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY_ORDER, variables.orderId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY_ORDERS] });
    },
  });
}
